/**
 * VERA 3D Evidence Viewer Component
 * Interactive 3D viewer with orbit controls for .ply and .glb evidence files
 * - PLY: Point cloud rendering (LiDAR scans, photogrammetry)
 * - GLB: Mesh/material rendering (textured 3D models, scene reconstructions)
 * Uses Three.js via @react-three/fiber and @react-three/drei
 */
import React, { Suspense, useRef, useState, useEffect, useMemo } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import {
  OrbitControls,
  Html,
  PerspectiveCamera,
} from "@react-three/drei";
import * as THREE from "three";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import {
  makeStyles,
  Text,
  Button,
  Spinner,
  Tooltip,
  ToggleButton,
} from "@fluentui/react-components";
import {
  ArrowReset24Regular,
  Cube24Regular,
  LineHorizontal124Regular,
} from "@fluentui/react-icons";

import type { EvidenceFormat } from "../types";

/** Sets the Three.js scene background to a light grey so it's not black */
const SceneSetup: React.FC = () => {
  const { scene } = useThree();
  useEffect(() => {
    scene.background = new THREE.Color("#f1f3f4");
  }, [scene]);
  return null;
};

const useStyles = makeStyles({
  container: {
    position: "relative" as const,
    width: "100%",
    height: "100%",
    minHeight: "500px",
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: "#f1f3f4",
  },
  canvas: {
    width: "100%",
    height: "100%",
  },
  toolbar: {
    position: "absolute" as const,
    top: "12px",
    right: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    zIndex: 10,
  },
  toolbarButton: {
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    borderRadius: "8px",
    minWidth: "36px",
    height: "36px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
  },
  toolbarButtonActive: {
    backgroundColor: "#004C97",
    color: "#ffffff",
    borderRadius: "8px",
    minWidth: "36px",
    height: "36px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
  },
  formatBadge: {
    position: "absolute" as const,
    top: "12px",
    left: "12px",
    zIndex: 10,
    backgroundColor: "#004C97",
    color: "#ffffff",
    padding: "4px 10px",
    borderRadius: "6px",
    fontSize: "11px",
    fontWeight: 600,
    fontFamily: "'Poppins', sans-serif",
    letterSpacing: "0.5px",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.15)",
  },
  infoBar: {
    position: "absolute" as const,
    bottom: "12px",
    left: "12px",
    display: "flex",
    gap: "12px",
    alignItems: "center",
    zIndex: 10,
    backgroundColor: "rgba(255, 255, 255, 0.85)",
    padding: "8px 16px",
    borderRadius: "8px",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
  },
  infoText: {
    fontSize: "12px",
    color: "#2D2926",
    fontFamily: "'Open Sans', sans-serif",
  },
  controls: {
    position: "absolute" as const,
    bottom: "12px",
    right: "12px",
    zIndex: 10,
    fontSize: "11px",
    color: "#8a8d91",
    fontFamily: "'Open Sans', sans-serif",
  },
});

// ── Demo Point Cloud (for PLY preview) ──────────────────────

const DemoPointCloud: React.FC = () => {
  const pointsRef = useRef<THREE.Points>(null);
  const [geometry] = useState(() => {
    const geo = new THREE.BufferGeometry();
    const count = 50000;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      const section = Math.random();
      if (section < 0.3) {
        // Floor
        positions[i3] = (Math.random() - 0.5) * 10;
        positions[i3 + 1] = Math.random() * 0.1;
        positions[i3 + 2] = (Math.random() - 0.5) * 10;
        colors[i3] = 0.2;
        colors[i3 + 1] = 0.22;
        colors[i3 + 2] = 0.25;
      } else if (section < 0.6) {
        // Walls
        const wall = Math.floor(Math.random() * 4);
        const h = Math.random() * 4;
        switch (wall) {
          case 0:
            positions[i3] = -5 + Math.random() * 0.1;
            positions[i3 + 1] = h;
            positions[i3 + 2] = (Math.random() - 0.5) * 10;
            break;
          case 1:
            positions[i3] = 5 - Math.random() * 0.1;
            positions[i3 + 1] = h;
            positions[i3 + 2] = (Math.random() - 0.5) * 10;
            break;
          case 2:
            positions[i3] = (Math.random() - 0.5) * 10;
            positions[i3 + 1] = h;
            positions[i3 + 2] = -5 + Math.random() * 0.1;
            break;
          default:
            positions[i3] = (Math.random() - 0.5) * 10;
            positions[i3 + 1] = h;
            positions[i3 + 2] = 5 - Math.random() * 0.1;
        }
        colors[i3] = 0.3;
        colors[i3 + 1] = 0.32;
        colors[i3 + 2] = 0.36;
      } else if (section < 0.8) {
        // Objects
        const cx = (Math.random() - 0.5) * 6;
        const cz = (Math.random() - 0.5) * 6;
        positions[i3] = cx + (Math.random() - 0.5) * 1.5;
        positions[i3 + 1] = Math.random() * 1.5;
        positions[i3 + 2] = cz + (Math.random() - 0.5) * 1.5;
        colors[i3] = 0.0;
        colors[i3 + 1] = 0.2;
        colors[i3 + 2] = 0.45;
      } else {
        // Ceiling
        positions[i3] = (Math.random() - 0.5) * 10;
        positions[i3 + 1] = 4 + Math.random() * 0.1;
        positions[i3 + 2] = (Math.random() - 0.5) * 10;
        colors[i3] = 0.25;
        colors[i3 + 1] = 0.27;
        colors[i3 + 2] = 0.3;
      }
    }

    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    return geo;
  });

  useFrame((_, delta) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y += delta * 0.02;
    }
  });

  return (
    <points ref={pointsRef} geometry={geometry}>
      <pointsMaterial
        size={0.03}
        vertexColors
        transparent
        opacity={0.8}
        sizeAttenuation
      />
    </points>
  );
};

// ── Demo GLB Mesh (for GLB preview when no file loaded) ─────

const DemoGlbMesh: React.FC = () => {
  const groupRef = useRef<THREE.Group>(null);

  useFrame((_, delta) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += delta * 0.15;
    }
  });

  return (
    <group ref={groupRef}>
      {/* Main building body */}
      <mesh position={[0, 1.25, 0]}>
        <boxGeometry args={[3, 2.5, 2.5]} />
        <meshStandardMaterial color="#5a6a7a" roughness={0.7} metalness={0.1} />
      </mesh>
      {/* Roof */}
      <mesh position={[0, 2.85, 0]} rotation={[0, Math.PI / 4, 0]}>
        <coneGeometry args={[2.3, 1.2, 4]} />
        <meshStandardMaterial color="#8b4513" roughness={0.8} />
      </mesh>
      {/* Door */}
      <mesh position={[0, 0.65, 1.26]}>
        <boxGeometry args={[0.7, 1.3, 0.05]} />
        <meshStandardMaterial color="#3a2a1a" roughness={0.9} />
      </mesh>
      {/* Windows */}
      {[[-0.8, 1.5, 1.26], [0.8, 1.5, 1.26]].map((pos, i) => (
        <mesh key={i} position={pos as [number, number, number]}>
          <boxGeometry args={[0.5, 0.5, 0.05]} />
          <meshStandardMaterial
            color="#87ceeb"
            roughness={0.1}
            metalness={0.3}
            transparent
            opacity={0.7}
          />
        </mesh>
      ))}
      {/* Ground plane */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <planeGeometry args={[8, 8]} />
        <meshStandardMaterial color="#4a5a3a" roughness={0.9} />
      </mesh>
      {/* Evidence markers */}
      {[
        [-1.5, 0.15, 2],
        [1.2, 0.15, 1.8],
        [0, 0.15, 2.5],
        [-2, 0.15, 0.5],
      ].map((pos, i) => (
        <group key={`marker-${i}`} position={pos as [number, number, number]}>
          <mesh>
            <coneGeometry args={[0.12, 0.3, 8]} />
            <meshStandardMaterial
              color="#ffcc00"
              emissive="#ffcc00"
              emissiveIntensity={0.3}
            />
          </mesh>
          <mesh position={[0, -0.05, 0]}>
            <cylinderGeometry args={[0.15, 0.15, 0.05, 16]} />
            <meshStandardMaterial color="#ffcc00" />
          </mesh>
        </group>
      ))}
    </group>
  );
};

// ── PLY Model Loader ────────────────────────────────────────

const PlyModel: React.FC<{
  url: string;
  onLoad?: (info: { vertices: number }) => void;
}> = ({ url, onLoad }) => {
  const [geometry, setGeometry] = useState<THREE.BufferGeometry | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loader = new PLYLoader();
    loader.load(
      url,
      (geo) => {
        geo.computeVertexNormals();
        geo.center();
        geo.computeBoundingBox();
        const box = geo.boundingBox;
        if (box) {
          const maxDim = Math.max(
            box.max.x - box.min.x,
            box.max.y - box.min.y,
            box.max.z - box.min.z
          );
          if (maxDim > 0) {
            const scale = 5 / maxDim;
            geo.scale(scale, scale, scale);
          }
        }
        setGeometry(geo);
        onLoad?.({ vertices: geo.attributes.position?.count || 0 });
      },
      undefined,
      (err) => {
        console.error("PLY load error:", err);
        setError("Failed to load PLY file");
      }
    );
  }, [url, onLoad]);

  if (error) {
    return (
      <Html center>
        <div style={{ color: "#ff6b6b", textAlign: "center" }}>
          <p>{error}</p>
        </div>
      </Html>
    );
  }

  if (!geometry) {
    return (
      <Html center>
        <Spinner size="large" />
      </Html>
    );
  }

  const hasColors = !!geometry.attributes.color;

  return (
    <points geometry={geometry}>
      <pointsMaterial
        size={0.02}
        vertexColors={hasColors}
        color={hasColors ? undefined : "#4a90d9"}
        transparent
        opacity={0.9}
        sizeAttenuation
      />
    </points>
  );
};

// ── GLB Model Loader ────────────────────────────────────────

const GlbModel: React.FC<{
  url: string;
  wireframe: boolean;
  onLoad?: (info: { vertices: number; meshes: number }) => void;
}> = ({ url, wireframe, onLoad }) => {
  const [scene, setScene] = useState<THREE.Group | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loader = new GLTFLoader();

    // Set up Draco decoder for compressed GLB files
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath("https://www.gstatic.com/draco/versioned/decoders/1.5.6/");
    loader.setDRACOLoader(dracoLoader);

    loader.load(
      url,
      (gltf) => {
        const loadedScene = gltf.scene;

        // Center and auto-scale the model
        const box = new THREE.Box3().setFromObject(loadedScene);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);

        loadedScene.position.sub(center);
        if (maxDim > 0) {
          const scale = 5 / maxDim;
          loadedScene.scale.multiplyScalar(scale);
        }

        // Count vertices and meshes
        let totalVertices = 0;
        let meshCount = 0;
        loadedScene.traverse((child) => {
          if ((child as THREE.Mesh).isMesh) {
            meshCount++;
            const mesh = child as THREE.Mesh;
            if (mesh.geometry.attributes.position) {
              totalVertices += mesh.geometry.attributes.position.count;
            }
          }
        });

        setScene(loadedScene);
        onLoad?.({ vertices: totalVertices, meshes: meshCount });

        // Cleanup Draco loader
        dracoLoader.dispose();
      },
      undefined,
      (err) => {
        console.error("GLB load error:", err);
        setError("Failed to load GLB file");
        dracoLoader.dispose();
      }
    );

    return () => {
      dracoLoader.dispose();
    };
  }, [url, onLoad]);

  // Apply wireframe toggle to all meshes in the scene
  useEffect(() => {
    if (!scene) return;
    scene.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        const material = mesh.material;
        if (Array.isArray(material)) {
          material.forEach((mat) => {
            if (mat instanceof THREE.MeshStandardMaterial || mat instanceof THREE.MeshBasicMaterial) {
              mat.wireframe = wireframe;
            }
          });
        } else if (
          material instanceof THREE.MeshStandardMaterial ||
          material instanceof THREE.MeshBasicMaterial
        ) {
          material.wireframe = wireframe;
        }
      }
    });
  }, [scene, wireframe]);

  if (error) {
    return (
      <Html center>
        <div style={{ color: "#ff6b6b", textAlign: "center" }}>
          <p>{error}</p>
        </div>
      </Html>
    );
  }

  if (!scene) {
    return (
      <Html center>
        <Spinner size="large" />
      </Html>
    );
  }

  return <primitive object={scene} />;
};

// ── Main Viewer Component ─────────────────────────────────────

interface EvidenceViewer3DProps {
  fileUrl?: string;
  /** File format: "ply" for point clouds, "glb" for mesh models */
  format?: EvidenceFormat;
  evidenceName?: string;
  vertexCount?: number;
  fileSizeMB?: number;
  /** If true, show demo content instead of loading file */
  demo?: boolean;
}

export const EvidenceViewer3D: React.FC<EvidenceViewer3DProps> = ({
  fileUrl,
  format = "ply",
  evidenceName,
  vertexCount,
  fileSizeMB,
  demo = true,
}) => {
  const styles = useStyles();
  const [wireframe, setWireframe] = useState(false);
  const controlsRef = useRef<any>(null);

  const isGlb = format === "glb";
  const isPly = format === "ply";
  const showDemo = demo || !fileUrl;

  const handleReset = () => {
    if (controlsRef.current) {
      controlsRef.current.reset();
    }
  };

  const formatLabel = isGlb ? "GLB" : "PLY";

  return (
    <div className={styles.container}>
      {/* Format Badge */}
      <div className={styles.formatBadge}>
        .{formatLabel} {isGlb ? "• 3D Model" : "• Point Cloud"}
      </div>

      <Canvas
        className={styles.canvas}
        gl={{ antialias: true, alpha: false, toneMapping: THREE.ACESFilmicToneMapping }}
      >
        <SceneSetup />
        <PerspectiveCamera makeDefault position={[8, 6, 8]} fov={50} />
        <OrbitControls
          ref={controlsRef}
          enableDamping
          dampingFactor={0.05}
          minDistance={1}
          maxDistance={50}
          enablePan
          panSpeed={0.5}
          rotateSpeed={0.5}
        />

        {/* Lighting — enhanced for GLB materials, no horizon/sky */}
        <ambientLight intensity={isGlb ? 0.6 : 0.5} />
        <directionalLight
          position={[10, 10, 5]}
          intensity={isGlb ? 1.2 : 0.8}
        />
        <directionalLight position={[-10, 5, -5]} intensity={isGlb ? 0.5 : 0.3} />
        {isGlb && (
          <directionalLight position={[0, 10, -10]} intensity={0.3} />
        )}

        {/* 3D Content */}
        <Suspense fallback={null}>
          {showDemo ? (
            isGlb ? <DemoGlbMesh /> : <DemoPointCloud />
          ) : isGlb ? (
            <GlbModel url={fileUrl!} wireframe={wireframe} />
          ) : (
            <PlyModel url={fileUrl!} />
          )}
        </Suspense>
      </Canvas>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <Tooltip content="Reset view" relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={<ArrowReset24Regular />}
            className={styles.toolbarButton}
            onClick={handleReset}
          />
        </Tooltip>
        {/* Wireframe toggle — only for GLB mesh models */}
        {isGlb && (
          <Tooltip
            content={wireframe ? "Solid view" : "Wireframe view"}
            relationship="label"
          >
            <Button
              appearance="subtle"
              size="small"
              icon={<LineHorizontal124Regular />}
              className={
                wireframe ? styles.toolbarButtonActive : styles.toolbarButton
              }
              onClick={() => setWireframe(!wireframe)}
            />
          </Tooltip>
        )}
      </div>

      {/* Info Bar */}
      <div className={styles.infoBar}>
        {evidenceName && (
          <Text className={styles.infoText}>{evidenceName}</Text>
        )}
        {vertexCount && (
          <Text className={styles.infoText}>
            {(vertexCount / 1000000).toFixed(1)}M vertices
          </Text>
        )}
        {fileSizeMB && (
          <Text className={styles.infoText}>
            {fileSizeMB >= 1024
              ? `${(fileSizeMB / 1024).toFixed(1)} GB`
              : `${fileSizeMB.toFixed(1)} MB`}
          </Text>
        )}
      </div>

      {/* Controls hint */}
      <div className={styles.controls}>
        Orbit: Left Click • Pan: Right Click • Zoom: Scroll
      </div>
    </div>
  );
};

/** @deprecated Use EvidenceViewer3D instead */
export const PlyViewer = EvidenceViewer3D;

export default EvidenceViewer3D;
